import type { FC, Dispatch, SetStateAction, MouseEvent } from 'react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Autocomplete,
  Dialog,
  Flex,
  Text,
  DropdownMenu,
  Button,
  Icon,
  Badge,
  Separator,
} from '@scale/scaleui-radix';
import { useGetCohorts, useGetCohortMappingsByConsumer } from '../../../../communication/apiClient';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { faGear, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { CohortDTO, CohortSubscriptionType } from '@scale/cohorts';
import { UpsertCohortDialog } from './UpsertCohortDialog';
import { Column } from '@scale/compass-shared/types';

export interface CohortOption {
  cohortId: string;
  cohortVersionId: string;
  cohortName: string;
  subscriptionType?: CohortSubscriptionType;
  versionNumber?: number;
}

interface CohortSelectorBaseProps {
  onMappingsChange?: Dispatch<SetStateAction<Map<string, CohortSubscriptionType>>>;
  disabled?: boolean;
  isReadOnly?: boolean;
  allowExactSubscription?: boolean;
  consumerId?: string;
  consumerType?: string;
  /** Enable "Create Cohort" action in dropdown */
  allowCreateCohort?: boolean;
  /** Current user ID (required when allowCreateCohort is true) */
  currentUserId?: string;
  /** Callback fired when a new cohort is created via the dialog */
  onCohortCreated?: (cohort: CohortOption) => void;
  /** Input columns to allow filtering on in the create cohort dialog */
  inputColumns?: Column[];
  /** Scoping columns to allow filtering on in the create cohort dialog */
  scopingColumns?: Column[];
}

interface SingleSelectProps extends CohortSelectorBaseProps {
  multiple?: false;
  value?: CohortOption;
  onValueChange?: (option: CohortOption | undefined) => void;
  /** Initialize from a cohortVersionId string (useful for filter adapters) */
  defaultCohortVersionId?: string;
}

interface MultiSelectProps extends CohortSelectorBaseProps {
  multiple: true;
  value?: CohortOption[];
  onValueChange?: (options: CohortOption[]) => void;
  /** Initialize from cohortVersionId strings (useful for filter adapters) */
  defaultCohortVersionIds?: string[];
  /** Initial subscription type per cohort version ID */
  defaultSubscriptionTypeByVersionId?: Record<string, CohortSubscriptionType>;
}

type CohortSelectorProps = SingleSelectProps | MultiSelectProps;

const CREATE_COHORT_VALUE = '__CREATE_COHORT__';

export const CohortSelector: FC<CohortSelectorProps> = props => {
  const {
    onMappingsChange,
    value,
    onValueChange,
    consumerId,
    consumerType,
    inputColumns,
    scopingColumns,
    disabled = false,
    isReadOnly = false,
    multiple = false,
    allowExactSubscription = true,
    allowCreateCohort = false,
    currentUserId,
    onCohortCreated,
  } = props;

  // Get default version IDs for initialization
  const defaultCohortVersionId = !multiple
    ? (props as SingleSelectProps).defaultCohortVersionId
    : undefined;
  const defaultCohortVersionIds = multiple
    ? (props as MultiSelectProps).defaultCohortVersionIds
    : undefined;
  const defaultSubscriptionTypeByVersionId = multiple
    ? (props as MultiSelectProps).defaultSubscriptionTypeByVersionId
    : undefined;

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue, 500);

  // Store onValueChange in a ref to avoid triggering effects when callback reference changes
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState<CohortOption | CohortOption[] | undefined>(
    multiple ? [] : undefined,
  );

  // Track the last initialized default value to detect changes
  const [lastInitializedDefault, setLastInitializedDefault] = useState<string | undefined>(
    undefined,
  );

  // State for create cohort dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Key to force Autocomplete remount when dialog closes (clears internal state)
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const prevCreateDialogOpen = useRef(false);

  // When dialog closes without creating a cohort, force Autocomplete remount to clear internal state
  useEffect(() => {
    if (prevCreateDialogOpen.current && !createDialogOpen) {
      setAutocompleteKey(k => k + 1);
    }
    prevCreateDialogOpen.current = createDialogOpen;
  }, [createDialogOpen]);

  // Compute current default key for comparison
  const currentDefaultKey = useMemo(() => {
    if (multiple && defaultCohortVersionIds?.length) {
      return defaultCohortVersionIds.join(',');
    }
    if (!multiple && defaultCohortVersionId) {
      return defaultCohortVersionId;
    }
    return undefined;
  }, [multiple, defaultCohortVersionId, defaultCohortVersionIds]);

  // Check if we need to (re)initialize
  const needsInitialization = currentDefaultKey !== lastInitializedDefault;

  // Determine version IDs to fetch for initialization from defaults
  const versionIdsToFetch = useMemo(() => {
    // Only fetch if we need to initialize
    if (!needsInitialization) return undefined;

    if (multiple && defaultCohortVersionIds?.length) {
      return defaultCohortVersionIds;
    }
    if (!multiple && defaultCohortVersionId) {
      return [defaultCohortVersionId];
    }
    return undefined;
  }, [multiple, defaultCohortVersionId, defaultCohortVersionIds, needsInitialization]);

  // Fetch cohort mappings by consumer if consumerId is provided
  const { data: cohortMappingsData } = useGetCohortMappingsByConsumer(
    consumerId ?? '',
    consumerType ?? '',
    { enabled: !!(consumerId && consumerType) },
  );

  // Fetch cohorts by version IDs for initialization from defaults
  const shouldFetchByVersionIds =
    !consumerId || (cohortMappingsData !== undefined && cohortMappingsData.mappings?.length === 0);
  const { data: cohortsByVersionData } = useGetCohorts(
    {
      cohortVersionIds: shouldFetchByVersionIds ? versionIdsToFetch : undefined,
      archived: false,
    },
    { enabled: shouldFetchByVersionIds && !!versionIdsToFetch && versionIdsToFetch.length > 0 },
  );

  // Initialize from default version IDs (or clear when defaults become empty)
  useEffect(() => {
    if (!needsInitialization) return;
    if (value !== undefined) return; // Don't initialize if controlled

    const versionIds = multiple
      ? defaultCohortVersionIds
      : defaultCohortVersionId
      ? [defaultCohortVersionId]
      : undefined;

    // Handle clearing when defaults become empty
    if (!versionIds?.length) {
      setLastInitializedDefault(currentDefaultKey);
      if (multiple) {
        setInternalValue([]);
        (onValueChangeRef.current as ((options: CohortOption[]) => void) | undefined)?.([]);
      } else {
        setInternalValue(undefined);
        (onValueChangeRef.current as ((option: CohortOption | undefined) => void) | undefined)?.(
          undefined,
        );
      }
      return;
    }

    const initializedOptions: CohortOption[] = [];
    let dataIsAvailable = false;

    if (consumerId && consumerType && cohortMappingsData?.mappings) {
      // Use cohort mappings data
      dataIsAvailable = true;
      versionIds.forEach(cohortVersionId => {
        const mapping = cohortMappingsData.mappings.find(
          m => m.cohortVersionId === cohortVersionId,
        );
        if (mapping) {
          initializedOptions.push({
            cohortId: mapping.cohortId,
            cohortVersionId: mapping.cohortVersionId,
            cohortName: mapping.cohortName,
            subscriptionType: mapping.subscriptionType as CohortSubscriptionType,
            versionNumber: mapping.versionNumber,
          });
        }
      });
    } else if (cohortsByVersionData?.data) {
      // Use cohorts fetched by version IDs
      dataIsAvailable = true;
      versionIds.forEach(cohortVersionId => {
        const cohort = cohortsByVersionData.data.find(
          (c: any) => c.latestVersionId?.toString() === cohortVersionId,
        );
        if (cohort) {
          initializedOptions.push({
            cohortId: cohort._id,
            cohortVersionId: cohort.latestVersionId?.toString() ?? '',
            cohortName: cohort.name,
            subscriptionType:
              defaultSubscriptionTypeByVersionId?.[cohortVersionId] ??
              CohortSubscriptionType.Latest,
            versionNumber: cohort.latestVersionNumber,
          });
        }
      });
    }

    // Only mark as initialized once we've actually attempted with available data
    // This prevents repeated effect runs when no matching cohorts are found
    if (!dataIsAvailable) {
      return; // Data not yet loaded, wait for it
    }

    // Mark as initialized regardless of whether matches were found
    setLastInitializedDefault(currentDefaultKey);

    if (initializedOptions.length > 0) {
      if (multiple) {
        setInternalValue(initializedOptions);
        (onValueChangeRef.current as ((options: CohortOption[]) => void) | undefined)?.(
          initializedOptions,
        );
      } else {
        setInternalValue(initializedOptions[0]);
        (onValueChangeRef.current as ((option: CohortOption | undefined) => void) | undefined)?.(
          initializedOptions[0],
        );
      }
    }
  }, [
    needsInitialization,
    currentDefaultKey,
    value,
    multiple,
    defaultCohortVersionId,
    defaultCohortVersionIds,
    consumerId,
    consumerType,
    cohortMappingsData,
    cohortsByVersionData,
  ]);

  const { data, refetch: refetchCohorts } = useGetCohorts(
    { search: debouncedSearchValue, archived: false },
    { enabled: false },
  );

  useEffect(() => {
    if (debouncedSearchValue.trim() !== '') {
      refetchCohorts();
    }
  }, [debouncedSearchValue, refetchCohorts]);

  const searchOptions: CohortOption[] = useMemo(() => {
    if (!data) return [];
    return data.data.map((cohort: any) => ({
      cohortId: cohort._id,
      cohortVersionId: cohort.latestVersionId?.toString() ?? '',
      cohortName: cohort.name,
      subscriptionType: CohortSubscriptionType.Latest,
      versionNumber: cohort.latestVersionNumber,
    }));
  }, [data]);

  // Get current value (controlled or internal)
  const currentValue = useMemo(() => {
    if (value !== undefined) return value;
    return internalValue;
  }, [value, internalValue]);

  // Build a map of selected options for quick lookup
  const selectedOptionsMap = useMemo(() => {
    const map = new Map<string, CohortOption>();
    if (multiple) {
      const options = (currentValue as CohortOption[] | undefined) ?? [];
      options.forEach(opt => map.set(opt.cohortVersionId, opt));
    } else {
      const option = currentValue as CohortOption | undefined;
      if (option) {
        map.set(option.cohortVersionId, option);
      }
    }
    return map;
  }, [currentValue, multiple]);

  // Get the string value for Autocomplete component
  const autocompleteValue = useMemo(() => {
    if (multiple) {
      const options = (currentValue as CohortOption[] | undefined) ?? [];
      return options.map(opt => opt.cohortVersionId);
    } else {
      const option = currentValue as CohortOption | undefined;
      return option?.cohortVersionId ?? '';
    }
    // Disable exhaustive deps warning because createDialogOpen is not a dependency of this hook,
    // but autocompleteValue needs to be updated when createDialogOpen changes so we clear the CREATE_COHORT_VALUE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue, multiple, createDialogOpen]);

  const handleAutocompleteChange = (newAutocompleteValue: string | string[]) => {
    if (multiple) {
      // Multi-select mode: newAutocompleteValue is string[]
      const newValueArray = Array.isArray(newAutocompleteValue)
        ? newAutocompleteValue
        : [newAutocompleteValue];
      // Check for create cohort trigger
      if (newValueArray.includes(CREATE_COHORT_VALUE)) {
        setCreateDialogOpen(true);
        if (value === undefined) {
          setInternalValue(currentValue as CohortOption[]);
        }
        (onValueChange as ((options: CohortOption[]) => void) | undefined)?.(
          currentValue as CohortOption[],
        );
        return; // Don't process this as a selection
      }
      const newOptions: CohortOption[] = [];

      newValueArray.forEach(cohortVersionId => {
        const existingOption = selectedOptionsMap.get(cohortVersionId);
        const option =
          existingOption || searchOptions.find(o => o.cohortVersionId === cohortVersionId);

        if (option) {
          newOptions.push(option);
        }
      });

      // Update mappings
      const previousOptions = (currentValue as CohortOption[] | undefined) ?? [];
      onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
        const newMappings = new Map(mappings);
        // Add new selections
        newOptions.forEach(option => {
          newMappings.set(
            option.cohortId,
            option.subscriptionType ?? CohortSubscriptionType.Latest,
          );
        });
        // Remove deselected cohorts
        previousOptions.forEach(option => {
          if (!newValueArray.includes(option.cohortVersionId)) {
            newMappings.delete(option.cohortId);
          }
        });
        return newMappings;
      });

      // Update state - in uncontrolled mode, always update internalValue
      if (value === undefined) {
        setInternalValue(newOptions);
      }
      // Also notify parent if callback provided
      (onValueChange as ((options: CohortOption[]) => void) | undefined)?.(newOptions);
    } else {
      // Single-select mode: newAutocompleteValue is string
      const newValueString = newAutocompleteValue as string;
      const existingOption = Array.from(selectedOptionsMap.values())[0];

      // Check for create cohort trigger
      if (newValueString === CREATE_COHORT_VALUE) {
        setCreateDialogOpen(true);
        // Reset value to prevent CREATE_COHORT_VALUE from persisting in autocomplete
        if (value === undefined) {
          setInternalValue(currentValue as CohortOption | undefined);
        }
        (onValueChange as ((option: CohortOption | undefined) => void) | undefined)?.(
          currentValue as CohortOption | undefined,
        );
        return;
      }

      let newOption: CohortOption | undefined;
      if (newValueString) {
        newOption =
          (existingOption?.cohortVersionId === newValueString ? existingOption : undefined) ||
          searchOptions.find(o => o.cohortVersionId === newValueString);
      }

      // Update mappings
      onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
        const newMappings = new Map(mappings);
        // Remove previous selection
        if (existingOption) {
          newMappings.delete(existingOption.cohortId);
        }
        // Add new selection
        if (newOption) {
          newMappings.set(
            newOption.cohortId,
            newOption.subscriptionType ?? CohortSubscriptionType.Latest,
          );
        }
        return newMappings;
      });

      // Update state - in uncontrolled mode, always update internalValue
      if (value === undefined) {
        setInternalValue(newOption);
      }
      // Also notify parent if callback provided
      (onValueChange as ((option: CohortOption | undefined) => void) | undefined)?.(newOption);
    }
  };

  const handleSubscriptionChange = (type: string, cohortVersionId?: string) => {
    let targetOption: CohortOption | undefined;
    if (multiple) {
      if (!cohortVersionId) return;
      targetOption = selectedOptionsMap.get(cohortVersionId);
    } else {
      targetOption = Array.from(selectedOptionsMap.values())[0];
    }

    if (!targetOption) return;

    const updatedOption = { ...targetOption, subscriptionType: type as CohortSubscriptionType };

    // Update mappings
    onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
      const newMappings = new Map(mappings);
      newMappings.set(targetOption!.cohortId, type as CohortSubscriptionType);
      return newMappings;
    });

    // Update state with the modified option
    if (multiple) {
      const currentOptions = (currentValue as CohortOption[] | undefined) ?? [];
      const newOptions = currentOptions.map(opt =>
        opt.cohortVersionId === updatedOption.cohortVersionId ? updatedOption : opt,
      );
      // In uncontrolled mode, always update internalValue
      if (value === undefined) {
        setInternalValue(newOptions);
      }
      // Also notify parent if callback provided
      (onValueChange as ((options: CohortOption[]) => void) | undefined)?.(newOptions);
    } else {
      // In uncontrolled mode, always update internalValue
      if (value === undefined) {
        setInternalValue(updatedOption);
      }
      // Also notify parent if callback provided
      (onValueChange as ((option: CohortOption | undefined) => void) | undefined)?.(updatedOption);
    }
  };

  // Handler for when a cohort is created via the dialog
  const handleCohortCreatedFromDialog = useCallback(
    (createdCohort?: CohortDTO) => {
      if (!createdCohort) {
        setCreateDialogOpen(false);
        return;
      }

      // Transform CohortDTO to CohortOption
      const newCohortOption: CohortOption = {
        cohortId: createdCohort._id,
        cohortVersionId: createdCohort.latestVersionId?.toString() ?? '',
        cohortName: createdCohort.name,
        subscriptionType: CohortSubscriptionType.Latest,
        versionNumber: createdCohort.latestVersionNumber,
      };

      // Auto-select the created cohort
      if (multiple) {
        const currentOptions = (currentValue as CohortOption[] | undefined) ?? [];
        const newOptions = [...currentOptions, newCohortOption];
        if (value === undefined) {
          setInternalValue(newOptions);
        }
        (onValueChange as ((options: CohortOption[]) => void) | undefined)?.(newOptions);
      } else {
        if (value === undefined) {
          setInternalValue(newCohortOption);
        }
        (onValueChange as ((option: CohortOption | undefined) => void) | undefined)?.(
          newCohortOption,
        );
      }

      // Update mappings
      onMappingsChange?.(mappings => {
        const newMappings = new Map(mappings);
        newMappings.set(newCohortOption.cohortId, CohortSubscriptionType.Latest);
        return newMappings;
      });

      // Close dialog and notify parent
      setCreateDialogOpen(false);
      onCohortCreated?.(newCohortOption);
    },
    [multiple, currentValue, value, onValueChange, onMappingsChange, onCohortCreated],
  );

  const filteredSearchOptions: CohortOption[] = useMemo(() => {
    // Filter out already selected cohorts by cohortVersionId
    return searchOptions.filter(opt => !selectedOptionsMap.has(opt.cohortVersionId));
  }, [searchOptions, selectedOptionsMap]);

  const getCohortDisplayName = (cohort: CohortOption | undefined) => {
    if (!cohort) return '';
    if (cohort.versionNumber && cohort.subscriptionType !== CohortSubscriptionType.Latest) {
      return `${cohort.cohortName} - V${cohort.versionNumber}`;
    }
    return cohort.cohortName;
  };

  const getCohortLinkProps = (cohortId: string) => ({
    href: `/corp/genai-ops-hub/allocation/cohorts?cohortId=${encodeURIComponent(cohortId)}`,
    target: '_blank' as const,
    rel: 'noopener noreferrer',
    onClick: (e: MouseEvent) => e.stopPropagation(),
    style: { color: 'inherit', textDecoration: 'none' },
  });

  if (isReadOnly) {
    const selectedOptionsArray = Array.from(selectedOptionsMap.values());
    return (
      <Flex direction="row" gap="2" width="100%" align="center" wrap="wrap">
        {selectedOptionsArray.length > 0 ? (
          selectedOptionsArray.map(option => (
            <Badge key={option.cohortVersionId}>
              <a {...getCohortLinkProps(option.cohortId)}>{getCohortDisplayName(option)}</a>
            </Badge>
          ))
        ) : (
          <Badge>{getCohortDisplayName(undefined)}</Badge>
        )}
      </Flex>
    );
  }

  return (
    <Flex direction="row" gap="2" width="100%" align="center">
      <Flex direction="column" width="100%">
        {/* Search Input */}
        <Autocomplete.Root
          key={autocompleteKey}
          multiple={multiple}
          value={autocompleteValue}
          onValueChange={handleAutocompleteChange}
          search={searchValue}
          onSearchChange={setSearchValue}
          disabled={disabled}
          renderValue={item => {
            const cohortId = selectedOptionsMap.get(item.value)?.cohortId ?? '';
            return (
              <Flex direction="row" gap="1" align="center">
                <a {...getCohortLinkProps(cohortId)}>{item.displayName}</a>
                <Separator orientation="vertical" />
                {allowExactSubscription && (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <Button variant="ghost">
                        <Icon icon={faGear} label="Add cohort" size="1" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                      <DropdownMenu.RadioGroup
                        value={selectedOptionsMap.get(item.value)?.subscriptionType}
                        onValueChange={type => handleSubscriptionChange(type, item.value)}
                      >
                        <DropdownMenu.RadioItem value={CohortSubscriptionType.Latest}>
                          <Text>Always use latest version</Text>
                        </DropdownMenu.RadioItem>
                        <DropdownMenu.RadioItem value={CohortSubscriptionType.Exact}>
                          <Text>Don't subscribe to updates</Text>
                        </DropdownMenu.RadioItem>
                      </DropdownMenu.RadioGroup>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                )}
              </Flex>
            );
          }}
        >
          <Autocomplete.Content>
            <Autocomplete.TextField placeholder="Search cohorts..." onPaste={() => {}}>
              <Autocomplete.Values />
              <Autocomplete.Reset />
            </Autocomplete.TextField>
            <Autocomplete.Items>
              {/* Create Cohort option - always at top */}
              {allowCreateCohort && (
                <Autocomplete.Item
                  key={CREATE_COHORT_VALUE}
                  value={CREATE_COHORT_VALUE}
                  keywords={['create', 'new', 'add', searchValue]}
                >
                  <Flex direction="row" gap="2" align="center">
                    <Icon icon={faPlus} label="Create" size="1" />
                    <Text color="accent">Create new cohort</Text>
                  </Flex>
                </Autocomplete.Item>
              )}
              {allowCreateCohort && <Separator orientation="horizontal" />}
              {/* Render selected items hidden so they're in itemsByValue for display name */}
              {Array.from(selectedOptionsMap.values()).map(selectedOption => (
                <Autocomplete.Item
                  key={`selected-${selectedOption.cohortVersionId}`}
                  value={selectedOption.cohortVersionId}
                >
                  {getCohortDisplayName(selectedOption)}
                </Autocomplete.Item>
              ))}
              {/* Render filtered (unselected) options as visible selectable items */}
              {filteredSearchOptions.map((option: CohortOption) => (
                <Autocomplete.Item
                  key={option.cohortVersionId}
                  value={option.cohortVersionId}
                  keywords={[option.cohortName, option.cohortId]}
                >
                  {option.cohortName}
                </Autocomplete.Item>
              ))}
            </Autocomplete.Items>
          </Autocomplete.Content>
        </Autocomplete.Root>
      </Flex>

      {/* Create Cohort Dialog */}
      {allowCreateCohort && currentUserId && (
        <Dialog.Root open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <UpsertCohortDialog
            initialCohort={undefined}
            onCohortCreated={handleCohortCreatedFromDialog}
            onExistingCohortClicked={() => {
              setCreateDialogOpen(false);
            }}
            currentUserId={currentUserId}
            onConfirmedClose={() => setCreateDialogOpen(false)}
            inputColumns={inputColumns}
            scopingColumns={scopingColumns}
          />
        </Dialog.Root>
      )}
    </Flex>
  );
};
